import { isValidIban } from "../strings/is-valid-iban";
import type { Enums, IncomingInvoiceUnpayableReason } from "../types";
import { getPayableAmounts, type ReducibleAmounts } from "./amounts";

/** One bank account of the contact an invoice is booked to. */
export interface PayableCreditorAccount {
	iban: string;
	bic: string | null;
	isDefault: boolean;
}

/**
 * What deciding whether an invoice can be paid, and how much, reads off it. The gross amount and
 * the Skonto amount are the ones it is paid on — after a Kürzung, see `getPayableTerms`.
 */
export interface PayableInvoice {
	state: Enums<"incomingInvoiceState">;
	kind: Enums<"incomingInvoiceKind">;
	currency: string;
	grossAmount: number | string | null;
	skontoRate: number | string | null;
	skontoAmount: number | string | null;
	skontoDeadline: string | null;
	/** The supplier's name — the contact's, else the one read off the invoice. */
	creditorName: string | null;
	/** The IBAN the invoice prints. */
	iban: string | null;
	bic: string | null;
	/** The bank accounts of the contact the invoice is booked to — the only accounts paid. */
	creditorAccounts: PayableCreditorAccount[];
	workspaceId: string | null;
	/** The account of the workspace the invoice is addressed to. */
	debtorIban: string | null;
}

/**
 * The first reason an invoice cannot go into a payment file, or null when it can. The export
 * modal shows it before the file is asked for, and the server refuses the file on it — one rule,
 * so the two never disagree.
 *
 * @param invoice - The invoice and the account of its workspace.
 */
export function getUnpayableReason(invoice: PayableInvoice): IncomingInvoiceUnpayableReason | null {
	if (invoice.state !== "approved") return "not-approved";
	if (invoice.kind === "credit-note") return "credit-note";
	if (invoice.currency !== "EUR") return "not-eur";
	if (!invoice.grossAmount || Number(invoice.grossAmount) <= 0) return "no-amount";
	if (!invoice.creditorName) return "no-creditor";
	if (getUnconfirmedIban(invoice)) return "unconfirmed-iban";
	if (!getCreditorAccount(invoice)) return "no-creditor-iban";
	if (!invoice.workspaceId || !invoice.debtorIban || !isValidIban(invoice.debtorIban)) {
		return "no-paying-account";
	}
	return null;
}

/**
 * The account a supplier is paid on, which is always one of its contact's: the account the
 * invoice prints once an approver has confirmed it onto the contact, else — when it prints none,
 * or one with wrong check digits — the contact's default account. An IBAN the contact does not
 * hold is never paid, which is what keeps a forged invoice's account out of the payment file.
 *
 * @param invoice - The invoice's IBAN and BIC, and its contact's accounts.
 * @returns The IBAN, compact and in capitals, with its BIC; null when no account qualifies.
 */
export function getCreditorAccount(
	invoice: Pick<PayableInvoice, "iban" | "bic" | "creditorAccounts">,
) {
	const printed = toCompactIban(invoice.iban);
	if (printed && isValidIban(printed)) {
		const account = findAccount(invoice.creditorAccounts, printed);
		return account ? { iban: printed, bic: account.bic ?? invoice.bic } : null;
	}
	for (const account of invoice.creditorAccounts) {
		const iban = toCompactIban(account.iban);
		if (account.isDefault && iban && isValidIban(iban)) return { iban, bic: account.bic };
	}
	return null;
}

/**
 * The valid IBAN an invoice prints that its contact does not hold yet — what an approver has to
 * confirm before the invoice is approved or paid — or null.
 *
 * @param invoice - The invoice's IBAN and its contact's accounts.
 */
export function getUnconfirmedIban(invoice: Pick<PayableInvoice, "iban" | "creditorAccounts">) {
	const printed = toCompactIban(invoice.iban);
	if (!printed || !isValidIban(printed)) return null;
	return findAccount(invoice.creditorAccounts, printed) ? null : printed;
}

function findAccount(accounts: PayableCreditorAccount[], iban: string) {
	for (const account of accounts) {
		if (toCompactIban(account.iban) === iban) return account;
	}
	return null;
}

function toCompactIban(iban: string | null) {
	return iban?.replace(/\s+/g, "").toUpperCase() || null;
}

/**
 * The Skonto still open on the day of payment: the printed amount, else the rate of the gross
 * amount. Null once the deadline has passed, when none is offered, or when it would leave nothing
 * to pay.
 *
 * @param invoice - The invoice's amounts and Skonto terms.
 * @param executionDate - The day the transfer is executed, `YYYY-MM-DD`.
 */
export function getOpenSkonto(
	invoice: Pick<PayableInvoice, "grossAmount" | "skontoRate" | "skontoAmount" | "skontoDeadline">,
	executionDate: string,
) {
	if (!invoice.skontoDeadline || invoice.skontoDeadline < executionDate) return null;
	const grossAmount = Number(invoice.grossAmount);
	const printed = invoice.skontoAmount === null ? null : Number(invoice.skontoAmount);
	const rate = invoice.skontoRate === null ? null : Number(invoice.skontoRate);
	const amount = printed ?? (rate === null ? null : Math.round(grossAmount * rate) / 100);
	if (!amount || amount <= 0 || amount >= grossAmount) return null;
	return amount;
}

/**
 * The gross amount and printed Skonto amount an invoice is paid on: the printed ones, scaled down
 * by an approver's Kürzung. A Skonto rate needs no scaling — it applies to the reduced gross.
 *
 * @param invoice - The printed amounts, the reduced net amount and the printed Skonto amount.
 */
export function getPayableTerms(
	invoice: ReducibleAmounts & { skontoAmount: number | string | null },
) {
	const amounts = getPayableAmounts(invoice);
	const skonto = invoice.skontoAmount === null ? null : Number(invoice.skontoAmount);
	return {
		grossAmount: amounts.grossAmount,
		skontoAmount:
			skonto === null || !amounts.isReduced
				? skonto
				: Math.round(skonto * amounts.ratio * 100) / 100,
	};
}

/**
 * What paying an invoice comes to before any offset: the gross amount it is paid on, less the
 * Skonto still open on the day of payment when that is taken off. The export transfers it, and
 * the outgoing invoices planned against the payment are taken off it.
 *
 * @param invoice - The printed amounts, a Kürzung's reduced net amount, and the Skonto terms.
 * @param isSkontoApplied - Whether the Skonto still open is taken off.
 * @param executionDate - The day the transfer is executed, `YYYY-MM-DD`.
 * @returns The gross amount paid on, the Skonto taken off, and what is paid — null amounts for an
 *   invoice without a gross amount.
 */
export function getPaymentAmount(
	invoice: ReducibleAmounts &
		Pick<PayableInvoice, "skontoRate" | "skontoAmount" | "skontoDeadline">,
	isSkontoApplied: boolean,
	executionDate: string,
) {
	const terms = getPayableTerms(invoice);
	const skontoAmount = isSkontoApplied
		? getOpenSkonto({ ...invoice, ...terms }, executionDate)
		: null;
	const grossAmount = terms.grossAmount;
	const amount =
		grossAmount === null
			? null
			: (Math.round(grossAmount * 100) - Math.round((skontoAmount ?? 0) * 100)) / 100;
	return { grossAmount, skontoAmount, amount };
}

/** An open outgoing invoice a payment may be offset against, and what it still owes. */
export interface OffsetCandidate {
	id: string;
	number: string;
	openAmount: number;
}

/**
 * Take the outgoing invoices picked off a payment, in the order picked: each covers what it still
 * owes, up to what is left of the payment. An outgoing invoice it does not reach is reported as
 * unused — picking it would offset nothing — and one that owes nothing more cannot be picked.
 *
 * @param amount - What the payment comes to before the offsets.
 * @param picked - The outgoing invoices picked, with their open amounts.
 * @returns What each one takes off, what is left to transfer, and the ones that took nothing.
 */
export function applyOffsets(amount: number, picked: OffsetCandidate[]) {
	let remaining = Math.round(amount * 100);
	const applied: Array<{ id: string; number: string; amount: number }> = [];
	const unused: string[] = [];
	for (const candidate of picked) {
		const open = Math.round(candidate.openAmount * 100);
		const take = Math.min(open, remaining);
		if (take <= 0) {
			unused.push(candidate.id);
			continue;
		}
		applied.push({ id: candidate.id, number: candidate.number, amount: take / 100 });
		remaining -= take;
	}
	return { applied, remaining: remaining / 100, unused };
}

/**
 * The remittance line a payment carries: the invoice number, and the outgoing invoices taken off
 * it — what the supplier needs to see on the statement to book the difference.
 *
 * @param number - The incoming invoice's number.
 * @param offsetNumbers - The numbers of the outgoing invoices offset against it.
 */
export function getPaymentRemittance(number: string, offsetNumbers: string[]) {
	if (!offsetNumbers.length) return number;
	return `${number} abzgl. Verrechnung ${offsetNumbers.join(", ")}`;
}

/** Today's date in Germany, `YYYY-MM-DD` — the day a payment file asks the bank to execute it. */
export function getPaymentExecutionDate(now = new Date()) {
	return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(now);
}
